import React, { useState } from 'react';
import { ReactComponent as VerticalDotsIcon } from 'assets/icons/vertical-dots.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import { EditIcon } from 'cokitchen-icons';
import StatusColor from 'components/status-badge';
import { ENUM_EXPLORE_CARD_STATUS } from 'utils/enums';
import { Dropdown, DropdownItem, DropdownList } from '@cokitchen/cokitchen-components';
import ArchivePostPrompt from 'components/modals/archive-post-prompt';
import { ExplorePostType } from 'models/explore-posts.model';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';
import { useExploreContext } from 'contexts/explore.context';
import { initialExplorePostData } from 'contexts/explore.context/context';

type Props = {
  exploreData: ExplorePostType;
  handleDelete: () => void;
};
const ExploreCard = ({ exploreData, handleDelete }: Props) => {
  const { setFormState, setContentUrl } = useExploreContext();
  const [showDropDownOptions, setShowDropDownOptions] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const navigate = useNavigate();

  const handleEditPost = () => {
    const editData = `?edit=true&postId=${exploreData?.id}`;
    if (exploreData?.type === 'photos') navigate(`${ROUTE_KEYS.PHOTO_CONTENT}${editData}`);
    if (exploreData?.type === 'video') navigate(`${ROUTE_KEYS.VIDEO_CONTENT}${editData}`);
  };

  const resetState = () => {
    setFormState(initialExplorePostData);
    setContentUrl([]);
  };

  return (
    <div className='rounded-lg border border-gray-1/60 drop-shadow-lg shadow-sm  overflow-hidden '>
      <div className='w-full h-32 relative'>
        {exploreData?.cover_image && (
          <img
            src={exploreData?.cover_image}
            alt='Explore card image'
            className='w-full object-cover h-full'
          />
        )}
        <span className='bg-black/30 w-full h-full absolute inset-0'></span>
        <span className='absolute bottom-3 left-2 transition-all'>
          {exploreData?.is_promo_post && (
            <StatusColor
              status={ENUM_EXPLORE_CARD_STATUS.PROMO}
              wrapperClassName='p-0.5 !px-2 !bg-gray-1'
              className='text-xs'
            />
          )}
        </span>
      </div>
      <div className='mx-3 my-4'>
        <div className='flex items-center gap-4 justify-between'>
          <p className='text-sm font-medium truncate'>{exploreData?.caption}</p>
          <Dropdown
            placement='top-end'
            visible={showDropDownOptions}
            onClickOutside={() => setShowDropDownOptions(false)}
            content={
              <button
                onClick={() => {
                  resetState();
                  setShowDropDownOptions((prev) => !prev);
                }}>
                <VerticalDotsIcon className='w-4 h-4  text-gray-6' />
              </button>
            }>
            <DropdownList className='drop-shadow-sm !w-24  text-gray-6 font-medium text-sm block'>
              <DropdownItem className='text-sm' onClick={handleEditPost}>
                <span className='flex items-center gap-2.5 font-semibold text-gray-6/70'>
                  <EditIcon />
                  Edit post
                </span>
              </DropdownItem>
              <DropdownItem
                className='text-sm'
                onClick={() => {
                  setOpenArchiveModal(true);
                  setShowDropDownOptions(false);
                }}>
                <span className='flex items-center gap-2.5 font-semibold text-gray-6/70'>
                  <ArchiveIcon />
                  Archive post
                </span>
              </DropdownItem>
            </DropdownList>
          </Dropdown>
        </div>
        {exploreData?.end_date && (
          <p className='font-medium text-sm mt-1 text-yellow-6'>
            Expires {format(new Date(exploreData?.end_date), 'PPP')}
          </p>
        )}
      </div>

      {openArchiveModal && (
        <ArchivePostPrompt
          isOpen={openArchiveModal}
          onClose={() => setOpenArchiveModal(false)}
          id={exploreData.id}
          handleDelete={handleDelete}
        />
      )}
    </div>
  );
};
export default ExploreCard;
